import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import firebaseService, { 
  subscribeToNotifications, 
  isSubscribedToNotifications,
  unsubscribeFromNotifications,
  onForegroundMessage 
} from '../services/firebaseService';

// Types for notification states
type NotificationState = 'not-supported' | 'denied' | 'default' | 'granted' | 'subscribed' | 'subscribing';

// Styled components with design similar to PWA button
const NotificationButtonContainer = styled.button<{ visible: boolean; notificationState: NotificationState }>`
  position: absolute;
  right: 16px;
  top: calc(50% + 210px);
  background: none;
  border: none;
  width: 48px;
  height: 48px;
  display: ${({ visible }) => visible ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  color: white;
  cursor: pointer;
  z-index: 3;
  padding: 6px;
  
  &:hover {
    opacity: 0.8;
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ToastNotification = styled.div<{ visible: boolean; type: 'success' | 'info' | 'error' | 'warning' }>`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ type }) => 
    type === 'success' ? 'rgba(46, 204, 113, 0.95)' :
    type === 'error' ? 'rgba(231, 76, 60, 0.95)' :
    type === 'warning' ? 'rgba(243, 156, 18, 0.95)' :
    'rgba(52, 152, 219, 0.95)'
  };
  color: white;
  padding: 12px 20px;
  border-radius: 25px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  z-index: 1000;
  font-size: 14px;
  font-weight: 500;
  opacity: ${({ visible }) => visible ? 1 : 0};
  transform: ${({ visible }) => 
    visible ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(-20px)'
  };
  transition: all 0.3s ease;
  pointer-events: none;
  max-width: 90vw;
  text-align: center;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
`;

// Platform detection
const isMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
         window.innerWidth <= 768;
};

// Check if notifications are supported
const isNotificationSupported = (): boolean => {
  return typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator;
};

// Get notification permission state
const getNotificationState = (): NotificationState => {
  if (!isNotificationSupported()) {
    return 'not-supported';
  }

  if (isSubscribedToNotifications()) {
    return 'subscribed';
  }

  switch (Notification.permission) {
    case 'granted':
      return 'granted';
    case 'denied':
      return 'denied';
    default:
      return 'default';
  }
};

// Register FCM token with backend
const registerTokenWithBackend = async (token: string): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('📤 Registering FCM token with backend...');
    
    const response = await fetch('/api/notification/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    const result = await response.json();
    
    if (response.ok && result.success) {
      console.log('✅ Token registered with backend successfully');
      console.log(`📊 Total subscriptions: ${result.totalSubscriptions}`);
      return { success: true };
    } else {
      console.error('❌ Failed to register token with backend:', result.error);
      return { success: false, error: result.error || 'Unknown error' };
    }
  } catch (error) {
    console.error('❌ Error registering token with backend:', error);
    return { success: false, error: 'Network error' };
  }
};

// Unregister FCM token from backend
const unregisterTokenFromBackend = async (token: string): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('📤 Unregistering FCM token from backend...');
    
    const response = await fetch('/api/notification/unsubscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    const result = await response.json();
    
    if (response.ok && result.success) {
      console.log('✅ Token unregistered from backend successfully');
      return { success: true };
    } else {
      console.error('❌ Failed to unregister token from backend:', result.error);
      return { success: false, error: result.error || 'Unknown error' };
    }
  } catch (error) {
    console.error('❌ Error unregistering token from backend:', error);
    return { success: false, error: 'Network error' };
  }
};

// Send welcome notification - DISABLED to prevent admin API calls from frontend
const sendWelcomeNotification = async (token: string): Promise<void> => {
  try {
    console.log('📤 Welcome notification will be sent by backend automatically');
    
    // ⚠️ SECURITY: Frontend MUST NOT call admin APIs
    // Welcome notification should be sent by backend when user subscribes
    // via the existing /api/notifications/subscribe endpoint
    
    console.log('✅ Backend will handle welcome notification');
  } catch (error) {
    console.log('⚠️ Welcome notification setup:', error);
  }
};

// Notification Button Component
interface NotificationButtonProps {
  visible: boolean;
  onSubscriptionChange?: (subscribed: boolean) => void;
}

const NotificationButton: React.FC<NotificationButtonProps> = ({ 
  visible, 
  onSubscriptionChange 
}) => {
  const [notificationState, setNotificationState] = useState<NotificationState>('not-supported');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [toast, setToast] = useState<{
    visible: boolean;
    message: string;
    type: 'success' | 'info' | 'error' | 'warning';
  }>({
    visible: false,
    message: '',
    type: 'info'
  });

  // Show toast notification
  const showToast = useCallback((message: string, type: 'success' | 'info' | 'error' | 'warning' = 'info') => {
    setToast({ visible: true, message, type });
    setTimeout(() => {
      setToast(prev => ({ ...prev, visible: false }));
    }, 4000);
  }, []);

  // Update notification state
  const updateNotificationState = useCallback(() => {
    const state = getNotificationState();
    setNotificationState(state);
    
    console.log('Notification state updated:', state);
    return state;
  }, []);

  // Initialize and setup listeners
  useEffect(() => {
    if (!isNotificationSupported()) {
      console.warn('Notifications not supported in this browser');
      return;
    }

    // Initial state check
    updateNotificationState();

    // Listen for permission changes
    const checkPermissionChange = () => {
      updateNotificationState();
    };

    // Check periodically for permission changes
    const interval = setInterval(checkPermissionChange, 5000);

    // Setup foreground message listener
    const unsubscribeForeground = onForegroundMessage((payload) => {
      console.log('Foreground notification received:', payload);
      
      // Show toast for foreground message
      const title = payload.notification?.title || 'Thông báo mới';
      const body = payload.notification?.body || 'Bạn có thông báo mới';
      showToast(`${title}: ${body}`, 'info');
      
      // Show browser notification if not visible
      if (document.hidden) {
        firebaseService.showLocalNotification(title, body);
      }
    });

    return () => {
      clearInterval(interval);
      unsubscribeForeground();
    };
  }, [updateNotificationState, showToast]);

  // Handle button click
  const handleButtonClick = useCallback(async () => {
    if (isSubscribing) {
      return; // Prevent multiple clicks during subscription
    }

    console.log('Notification button clicked, current state:', notificationState);

    try {
      if (notificationState === 'subscribed') {
        // Unsubscribe from notifications
        setIsSubscribing(true);
        showToast('🔕 Đang hủy đăng ký...', 'info');
        
        const fcmResult = await unsubscribeFromNotifications();
        if (fcmResult) {
          // Also unregister from backend
          const currentToken = localStorage.getItem('fcm_token');
          if (currentToken) {
            await unregisterTokenFromBackend(currentToken);
          }
          
          setNotificationState('granted');
          showToast('✅ Đã hủy đăng ký thông báo', 'success');
          onSubscriptionChange?.(false);
        } else {
          showToast('❌ Không thể hủy đăng ký', 'error');
        }
        setIsSubscribing(false);
        return;
      }

      if (notificationState === 'default' || notificationState === 'denied') {
        // Request permission first
        showToast('🔔 Vui lòng cho phép thông báo', 'info');
        
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          showToast('❌ Cần cấp quyền thông báo để tiếp tục', 'warning');
          updateNotificationState();
          return;
        }
        
        updateNotificationState();
        // Don't subscribe yet, let user click again
        showToast('✅ Đã cấp quyền! Nhấn lại để đăng ký', 'success');
        return;
      }

      if (notificationState === 'granted') {
        // Subscribe to notifications
        setIsSubscribing(true);
        setNotificationState('subscribing');
        showToast('📱 Đang đăng ký thông báo...', 'info');
        
        const fcmResult = await subscribeToNotifications();
        
        if (fcmResult.success && fcmResult.token) {
          // Register token with backend
          const backendResult = await registerTokenWithBackend(fcmResult.token);
          
          if (backendResult.success) {
            setNotificationState('subscribed');
            showToast('🎉 Đăng ký thành công! Chờ thông báo chào mừng', 'success');
            onSubscriptionChange?.(true);
            
            // Send welcome notification after a short delay
            setTimeout(() => {
              sendWelcomeNotification(fcmResult.token!);
            }, 2000);
          } else {
            // FCM successful but backend failed
            setNotificationState('granted');
            showToast(`⚠️ Đăng ký một phần thành công: ${backendResult.error}`, 'warning');
          }
        } else {
          setNotificationState('granted');
          showToast(`❌ Đăng ký thất bại: ${fcmResult.error}`, 'error');
        }
        
        setIsSubscribing(false);
      }
    } catch (error) {
      console.error('Error handling notification button click:', error);
      showToast('❌ Có lỗi xảy ra', 'error');
      setIsSubscribing(false);
      updateNotificationState();
    }
  }, [notificationState, isSubscribing, showToast, onSubscriptionChange, updateNotificationState]);

  // Don't render if not supported or not mobile
  if (!isNotificationSupported() || !isMobile()) {
    return null;
  }

  const getButtonTitle = () => {
    switch (notificationState) {
      case 'default':
        return 'Đăng ký thông báo';
      case 'denied':
        return 'Cấp quyền thông báo';
      case 'granted':
        return 'Đăng ký thông báo';
      case 'subscribing':
        return 'Đang đăng ký...';
      case 'subscribed':
        return 'Hủy thông báo';
      default:
        return 'Thông báo';
    }
  };

  const getButtonAriaLabel = () => {
    switch (notificationState) {
      case 'default':
      case 'denied':
        return 'Yêu cầu quyền gửi thông báo';
      case 'granted':
        return 'Đăng ký nhận thông báo';
      case 'subscribing':
        return 'Đang đăng ký thông báo';
      case 'subscribed':
        return 'Hủy đăng ký thông báo';
      default:
        return 'Quản lý thông báo';
    }
  };

  return (
    <>
      <NotificationButtonContainer
        visible={visible}
        notificationState={isSubscribing ? 'subscribing' : notificationState}
        onClick={handleButtonClick}
        aria-label={getButtonAriaLabel()}
        title={getButtonTitle()}
        disabled={isSubscribing}
      >
        {isSubscribing ? (
          // Loading spinner
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3"/>
            <path d="M12 2 A10 10 0 0 1 22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <animateTransform
                attributeName="transform"
                attributeType="XML"
                type="rotate"
                from="0 12 12"
                to="360 12 12"
                dur="1s"
                repeatCount="indefinite"
              />
            </path>
          </svg>
        ) : notificationState === 'subscribed' ? (
          // ✅ FIXED: Better subscribed icon with unsubscribe indication  
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="9" cy="9" r="2" fill="currentColor" opacity="0.8"/>
            <path d="M7 9l1 1 2-2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 6l2 2M18 6l-2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.6"/>
          </svg>
        ) : (notificationState === 'denied' || notificationState === 'default') ? (
          // Permission needed icon (bell with exclamation)
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="8" r="1" fill="currentColor"/>
            <path d="M12 12v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        ) : (
          // Ready to subscribe icon (bell)
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </NotificationButtonContainer>

      <ToastNotification
        visible={toast.visible}
        type={toast.type}
      >
        {toast.message}
      </ToastNotification>
    </>
  );
};

export default NotificationButton; 